import React, { useState, FC } from 'react';
import { Modal } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { KTSVG } from '../../../../_metronic/helpers'; // Assuming KTSVG is available for icons

interface UploadInvoiceModalProps {
    show: boolean;
    handleClose: () => void;
    handleUpload: (file: File) => void; // Function to handle the upload logic
    paymentId: number; // To identify which payment this invoice is for
    isLoading: boolean; // To show loading state during upload
}

const UploadInvoiceModal: FC<UploadInvoiceModalProps> = ({ show, handleClose, handleUpload, paymentId, isLoading }) => {
    const { formatMessage } = useIntl();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [error, setError] = useState<string | null>(null);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setError(null); // Clear previous errors
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            // Basic validation (e.g., file type, size) can be added here
            // For now, just accept PDF
            if (file.type !== 'application/pdf') {
                setError(formatMessage({ id: 'REPORTS.PAYMENT.UPLOAD.INVALID_TYPE' }));
                setSelectedFile(null);
                return;
            }
            // Add size validation if needed
            // if (file.size > MAX_FILE_SIZE) { ... }

            setSelectedFile(file);
        } else {
            setSelectedFile(null);
        }
    };

    const onUploadClick = () => {
        if (selectedFile) {
            handleUpload(selectedFile);
        } else {
            setError(formatMessage({ id: 'REPORTS.PAYMENT.UPLOAD.NO_FILE' }));
        }
    };

    // Reset state when modal closes
    const onModalHide = () => {
        setSelectedFile(null);
        setError(null);
        handleClose(); // Call the original handleClose
    }

    return (
        <Modal show={show} onHide={onModalHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>{formatMessage({ id: 'REPORTS.PAYMENT.UPLOAD.TITLE' })}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <div className="alert alert-danger">{error}</div>}
                <div className="mb-3">
                    <label htmlFor="invoiceFile" className="form-label">
                        {formatMessage({ id: 'REPORTS.PAYMENT.UPLOAD.SELECT_LABEL' })} (PDF only)
                    </label>
                    <input
                        type="file"
                        className="form-control"
                        id="invoiceFile"
                        accept="application/pdf" // Restrict file types
                        onChange={handleFileChange}
                        disabled={isLoading}
                    />
                </div>
                {selectedFile && (
                    <div className="mt-2">
                        <strong>{formatMessage({ id: 'REPORTS.PAYMENT.UPLOAD.SELECTED_FILE' })}:</strong> {selectedFile.name}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-light" onClick={onModalHide} disabled={isLoading}>
                    {formatMessage({ id: 'MODAL.CANCEL' })}
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={onUploadClick}
                    disabled={!selectedFile || isLoading}
                >
                    {isLoading ? (
                        <>
                            <span className='spinner-border spinner-border-sm align-middle me-2'></span>
                            {formatMessage({ id: 'MODAL.UPLOADING' })}...
                        </>
                    ) : (
                        formatMessage({ id: 'MODAL.UPLOAD' })
                    )}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default UploadInvoiceModal;
